import React from "react";
import { Product } from "../../Payment/types/payment";
import PaymentActions from "./PaymentActions";
import { Divider, Checkbox } from "@mui/material";
import { User } from "lucide-react";
import { usePaymentCalculations } from "../hooks/usePaymentCalculations";
import PaymentInputField from "./PaymentInputField";
import CommonButtons from "src/shared/components/CommonButtons";
import { LedgerDetails } from "src/shared/services/interfacev2";

interface PaymentSummaryProps {
  products: Product[];
  customer: LedgerDetails | null;
  paymentMethod: "Cash" | "Buy on Credit" | "Card";
  receivedAmount: number;
  showBalance: boolean;
  currentTime: { hours: string; minutes: string; day: string; month: string; year: string };
  onAmountChange: (value: number) => void;
  onToggleBalance: () => void;
  roundAmount: (value: any) => string;
  onCancel: () => void;
  onComplete: () => void;
  isLoading?: boolean;
  backHome: (value: string) => void;
  buttonLoading?: boolean;
  deliveryFeeEnabled: boolean;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  products,
  customer,
  paymentMethod,
  receivedAmount,
  showBalance,
  currentTime,
  onAmountChange,
  onToggleBalance,
  roundAmount,
  onCancel,
  onComplete,
  isLoading = false,
  backHome,
  buttonLoading = false,
  deliveryFeeEnabled,
}) => {
  const { orderAmount, discount, deliveryFee, finalAmount, outstandingBalance, totalPayable, remainingAmount } = usePaymentCalculations(
    products,
    customer,
    showBalance,
    receivedAmount
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    if (value > totalPayable) value = totalPayable;
    onAmountChange(value);
  };

  const orderPriceData = Number(outstandingBalance) + Number(orderAmount)

  return (
    <div className='text-left -pb-5'>
      <div className='flex justify-end mb-2 -mt-4'>
        <CommonButtons label='Add Product' onClick={() => backHome("false")} className='w-36' />
      </div>
      <div className='flex items-center gap-3 bg-gray-100 p-3 px-5 mb-2 rounded-md'>
        <div className='w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full'>
          <User className='text-gray-600' size={22} />
        </div>
        <div className='flex flex-col text-sm'>
          <span className='font-semibold'>Customer: {(customer?.customerDetails.firstName ?? "") + (customer?.customerDetails.middleName ?? "") + (customer?.customerDetails.lastName ?? "") || "N/A"}</span>
          <span className='text-gray-600'>Payment: {paymentMethod}</span>
          <span className='text-gray-500 text-xs'>
            Date: {currentTime.year}-{currentTime.month}-{currentTime.day} | {currentTime.hours}:{currentTime.minutes}
          </span>
        </div>
      </div>

      {/* Order Summary */}
      <div className='mb-4'>
        <h3 className='font-semibold text-gray-700 mb-1'>Order Summary</h3>
        <Divider className='mb-1.5' />
        {paymentMethod !== "Buy on Credit" && (
          <div className='flex items-center justify-between text-sm'>
            <div className=''>
              <Checkbox onChange={onToggleBalance} checked={showBalance} size='small' disabled={(Number(outstandingBalance) < 0)} />
              <span className='font-medium'>Outstanding Balance</span>
            </div>
            <span className='font-medium'>{roundAmount(outstandingBalance)} QAR</span>
          </div>
        )}
        <div className='space-y-2 text-sm'>
          {paymentMethod === "Buy on Credit" && (
            <div className='flex mt-2 justify-between'>
              <span className='px-10'>Outstanding Balance</span>
              <span className='font-medium'>{roundAmount(outstandingBalance)} QAR</span>
            </div>
          )}
          <div className='flex justify-between'>
            <span className='px-10'>Order Amount</span>
            <span className='font-medium'>{roundAmount(orderAmount)} QAR</span>
          </div>
          <div className='flex justify-between'>
            <span className='px-10'>Discount</span>
            <span>- {roundAmount(discount)} QAR</span>
          </div>
          {deliveryFeeEnabled && (
            <div className='flex justify-between'>
              <span className='px-10'>Delivery Fee</span>
              <span>{roundAmount(deliveryFee)} QAR</span>
            </div>
          )}
          <div className='flex justify-between'>
            <span className='px-10'>Final Amount</span>
            <span>{roundAmount(finalAmount)} QAR</span>
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div className='mb-2'>
        <h3 className='font-semibold text-gray-700 mb-1'>Payment Status</h3>
        <Divider className='mb-2' />
        <div className='space-y-2 text-sm'>
          <div className='flex justify-between'>
            <span className='px-10 mt-2'>Total Payable</span>
            <span className='font-medium mt-2'>{roundAmount(totalPayable)} QAR</span>
          </div>

          {/* Editable input only when Cash */}
          {paymentMethod === "Buy on Credit" ? (
            <PaymentInputField label='Received Amount' value={0} roundAmount={roundAmount} isEditable={false} />
          ) : (
            <PaymentInputField label='Received Amount' value={receivedAmount} onChange={handleInputChange} roundAmount={roundAmount} isEditable={(orderPriceData) <= 0 ? false : true} />
          )}
          <PaymentInputField label='Remaining Amount' value={remainingAmount} roundAmount={roundAmount} isEditable={false} />
        </div>
      </div>

      {/* Actions */}
      <Divider className='my-2' />
      <div className='flex justify-center -mt-1 -mb-3'>
        <PaymentActions
          onCancel={onCancel}
          onComplete={onComplete}
          isLoading={isLoading}
          backHomepay={() => { }}
          receivedAmount={receivedAmount}
          paymentMethod={paymentMethod}
          buttonLoading={buttonLoading}
          orderPriceData={orderPriceData}
        />
      </div>
    </div>
  );
};

export default PaymentSummary;
